/**
 * Integrytas Group - Base App (MOBILE)
 * Page: IKE.KEYS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/IkeKeysPage.tsx. A DISTINCT
 * presentation (KPI summary + dense card list + status facet chips + bottom-sheet
 * detail) over the SAME data/logic layer only:
 *   - reads via apiDb (ike_key most-recent keys + the ike_stats RPC roll-up, plus
 *     a csm_user_master name lookup so the caller shows as a name, never a raw id),
 *   - purely READ-ONLY: the idempotency barrier is observability. The desktop page's
 *     "Purge expired" is a low-frequency maintenance action kept on the desktop, so
 *     this screen imports no write path at all (no routed_writes, no apiDb mutation).
 *
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, KeyRound, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill, type MobileLeadTone, type MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

type KeyStatus = 'LOCKED' | 'COMPLETED' | 'FAILED';

interface MobileKeyRow {
  // idempotency_key is the natural business key of the row (a string token), not a
  // surrogate id — safe to surface. workspace_code is a technical ASF key with no
  // named entity behind it. user_id is a surrogate id and is deliberately NOT shown;
  // the resolved holder name is shown instead.
  idempotency_key: string;
  user_id: number;
  workspace_code: string;
  request_kind: string | null;
  request_endpoint: string | null;
  status: KeyStatus;
  response_status: number | null;
  locked_at: string;
  completed_at: string | null;
  expires_at: string;
}

interface Stats {
  locked: number;
  completed: number;
  failed: number;
  expired: number;
  total: number;
}

interface UserRow {
  user_id: number;
  display_name: string | null;
  email: string;
}

// Key status (CHECK enum) → localized label + pill/lead classes (§17.B10/B11).
// Defined once as a labelled constant — never bare literals scattered in JSX.
const STATUS_META: Record<KeyStatus, { labelKey: string; label: string; pillTone: MobilePillTone; leadTone: MobileLeadTone }> = {
  LOCKED: { labelKey: 'mobile.ike.status.LOCKED', label: 'Locked', pillTone: 'wait', leadTone: 'amber' },
  COMPLETED: { labelKey: 'mobile.ike.status.COMPLETED', label: 'Completed', pillTone: 'ok', leadTone: 'green' },
  FAILED: { labelKey: 'mobile.ike.status.FAILED', label: 'Failed', pillTone: 'no', leadTone: 'red' },
};

const STATUS_ORDER: KeyStatus[] = ['LOCKED', 'COMPLETED', 'FAILED'];

// Most-recent keys only — mirrors the desktop cap of 200.
const KEY_LIMIT = 200;

type StatusFacet = 'all' | KeyStatus;

export function MobileIkeKeysPage({ session: _session }: MobilePageComponentProps) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [keys, setKeys] = useState<MobileKeyRow[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('all');
  const [selected, setSelected] = useState<MobileKeyRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [keyRes, statsRes] = await Promise.all([
        apiDb
          .from('ike_key')
          .select(
            'idempotency_key, user_id, workspace_code, request_kind, request_endpoint, status, response_status, locked_at, completed_at, expires_at',
          )
          .order('locked_at', { ascending: false })
          .limit(KEY_LIMIT),
        apiDb.rpc<Stats>('ike_stats'),
      ]);
      if (keyRes.error) throw keyRes.error;

      const rows = (keyRes.data ?? []) as MobileKeyRow[];
      setKeys(rows);
      setStats((statsRes.data ?? null) as Stats | null);

      // Resolve caller ids to display names (§17.B4 — never the raw id).
      const ids = Array.from(new Set(rows.map((r) => r.user_id).filter((x): x is number => Boolean(x))));
      if (ids.length) {
        const { data: us, error: uErr } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', ids);
        if (uErr) throw uErr;
        const names: Record<number, string> = {};
        for (const u of (us ?? []) as UserRow[]) {
          names[u.user_id] = u.display_name || u.email || t('common.unknown', 'Unknown');
        }
        setUserNames(names);
      } else {
        setUserNames({});
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Load on mount. No _tl display resolution here, so no locale dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, []);

  const userText = (r: MobileKeyRow) =>
    r.user_id ? (userNames[r.user_id] ?? t('common.unknown', 'Unknown')) : t('common.system', 'System');

  const requestText = (r: MobileKeyRow) => {
    const parts = [r.request_kind, r.request_endpoint].filter(Boolean).join(' ');
    return parts || t('common.none', 'None');
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return keys.filter((r) => {
      if (statusFacet !== 'all' && r.status !== statusFacet) return false;
      if (!q) return true;
      const name = r.user_id ? (userNames[r.user_id] ?? '') : '';
      return (
        r.idempotency_key.toLowerCase().includes(q) ||
        (r.workspace_code ?? '').toLowerCase().includes(q) ||
        (r.request_endpoint ?? '').toLowerCase().includes(q) ||
        (r.request_kind ?? '').toLowerCase().includes(q) ||
        name.toLowerCase().includes(q)
      );
    });
  }, [keys, filter, statusFacet, userNames]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ike.all', 'All') },
    ...STATUS_ORDER.map((s) => ({ key: s as StatusFacet, label: t(STATUS_META[s].labelKey, STATUS_META[s].label) })),
  ];

  const shortKey = (key: string) => (key.length > 22 ? `${key.slice(0, 22)}…` : key);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.ike.title', 'Idempotency Keys')}
        description={t('mobile.ike.description', 'Recent idempotency barrier activity across the gateway.')}
      />

      <div className="m-kpis">
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.ike.kpi_locked', 'Active locks')}</div>
          <div className="m-kpi-v">{(stats?.locked ?? 0).toLocaleString()}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.ike.kpi_completed', 'Cached')}</div>
          <div className="m-kpi-v">{(stats?.completed ?? 0).toLocaleString()}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.ike.kpi_failed', 'Failed')}</div>
          <div className="m-kpi-v">{(stats?.failed ?? 0).toLocaleString()}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.ike.kpi_expired', 'Expired')}</div>
          <div className="m-kpi-v">{(stats?.expired ?? 0).toLocaleString()}</div>
        </div>
        <div className="m-kpi">
          <div className="m-kpi-l">{t('mobile.ike.kpi_total', 'Total')}</div>
          <div className="m-kpi-v">{(stats?.total ?? 0).toLocaleString()}</div>
        </div>
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.ike.search', 'Search keys')}
          aria-label={t('mobile.ike.search', 'Search keys')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.ike.none', 'No idempotency keys')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const meta = STATUS_META[r.status];
            return (
              <MobileCardRow
                key={`${r.idempotency_key}:${r.user_id}:${r.workspace_code}`}
                lead={<KeyRound />}
                leadTone={meta.leadTone}
                title={shortKey(r.idempotency_key)}
                subtitle={`${userText(r)} · ${formatDateTime(r.locked_at)}`}
                trailing={<MobileStatusPill tone={meta.pillTone}>{t(meta.labelKey, meta.label)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.ike.detail', 'Idempotency key detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><KeyRound size={20} /></span>
              <span>
                <div className="m-nm">{shortKey(selected.idempotency_key)}</div>
                <div className="m-em">{userText(selected)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.ike.status_lbl', 'Status')}
                value={
                  <MobileStatusPill tone={STATUS_META[selected.status].pillTone}>
                    {t(STATUS_META[selected.status].labelKey, STATUS_META[selected.status].label)}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.ike.caller', 'Caller')} value={userText(selected)} />
              {/* fk-display-ok: ASF workspace technical key, no named entity behind it */}
              <MobileDetailRow label={t('mobile.ike.workspace', 'Workspace')} value={selected.workspace_code || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.ike.request', 'Request')} value={requestText(selected)} />
              {selected.response_status != null && (
                <MobileDetailRow label={t('mobile.ike.response', 'Response')} value={selected.response_status} />
              )}
              <MobileDetailRow label={t('mobile.ike.claimed', 'Claimed')} value={formatDateTime(selected.locked_at)} />
              {selected.completed_at && (
                <MobileDetailRow label={t('mobile.ike.completed', 'Completed')} value={formatDateTime(selected.completed_at)} />
              )}
              <MobileDetailRow label={t('mobile.ike.expires', 'Expires')} value={formatDateTime(selected.expires_at)} />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('mobile.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
